import { useEffect, useState } from "react";

import { Observation } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Flex } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { getEditedByReference } from "@/encounters/components/EncounterLabor/services/labor";
import { longDateFormat } from "@/utils/datetime";
import { getPractitionerName } from "@/utils/medplum";

interface LastUpdatedProps {
  observation: Observation;
  showPreviousVersions: boolean;
}

export default function LastUpdated({ observation, showPreviousVersions }: LastUpdatedProps) {
  const medplum = useMedplum();

  const editedByReference = getEditedByReference(observation);
  const fallbackPerformerId = observation.performer?.[0]?.reference;
  const [performer, setPerformer] = useState<string>("Unknown");

  useEffect(() => {
    const fetchPractitioner = async () => {
      const ref = editedByReference || fallbackPerformerId;
      if (ref) {
        const id = ref.split("/")[1];
        const [name] = await getPractitionerName(medplum, id);
        setPerformer(name);
      }
    };
    fetchPractitioner();
  }, [medplum, editedByReference, fallbackPerformerId]);

  // Prefer clinical effectiveDateTime; fall back like row grouping when the bot omitted it
  // (e.g. QR missing date/time) so "Last Updated … on" still shows meta.lastUpdated.
  const formattedDate = longDateFormat(
    observation.effectiveDateTime ?? observation.issued ?? observation.meta?.lastUpdated,
  );

  return (
    <Flex mt="xs" align="center">
      <TextWrapper fz="sm">Last Updated by</TextWrapper>
      <TextWrapper fz="sm" mx="xs" variant="bold">
        {performer}
      </TextWrapper>
      <TextWrapper fz="sm">on {formattedDate}</TextWrapper>
      {showPreviousVersions && (
        <>
          <TextWrapper fz="sm" mx="xl" variant="bold">
            |
          </TextWrapper>
          <TextWrapper fz="sm" c="dark.4" variant="bold">
            Previous Versions
          </TextWrapper>
        </>
      )}
    </Flex>
  );
}
